<template>
  <div>
    <h1 class="flex mb-8 font-bold text-3xl">
      <icon name="store-front" class="w-6 mr-3 fill-gray-700" /> Campanhas
    </h1>

    <div class="mb-6 flex">
      <text-input v-model="form.search" class="w-full max-w-md mr-auto" placeholder="Buscar..." @reset="reset" />

      <button class="btn-indigo mr-4" @click="postMessages">
        <span>Enviar</span>
        <span class="hidden md:inline">Mensagens</span>
      </button>

      <inertia-link class="btn-indigo" :href="route('campaigns.create')">
        <span>Criar</span>
        <span class="hidden md:inline">Campanha</span>
      </inertia-link>
    </div>

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">#</th>
          <th class="px-6 pt-6 pb-4">Nome</th>
          <th class="px-6 pt-6 pb-4">Views</th>
          <th class="px-6 pt-6 pb-4">Cidade/UF</th>
          <th class="px-6 pt-6 pb-4">Período</th>
          <th class="px-6 pt-6 pb-4">Interação</th>
          <th class="px-6 pt-6 pb-4">URL</th>
          <th class="px-6 pt-6 pb-4">Criado Em</th>
          <th v-if="showOrganizations" class="px-6 pt-6 pb-4">Organização</th>
          <th class="px-6 pt-6 pb-4" colspan="2">Ativo</th>
        </tr>

        <tr v-for="campaign in campaigns.data" :key="campaign.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="flex border-t w-full h-full">
            <label v-if="campaign.message" class="form-label my-4 text-center" :class="{'mx-auto': campaign.deleted_at, 'ml-auto mr-0': !campaign.deleted_at}">
              <input type="checkbox" @change="setMessage($event, campaign.id)" />
            </label>

            <button
              v-if="!campaign.deleted_at && campaign.message"
              class="hover:underline my-4"
              tabindex="-1"
              type="button"
              :class="{'mx-auto': !campaign.message, 'ml-2 mr-auto': campaign.message}"
              @click="destroy(campaign.id)"
            >
              <icon name="trash" class="flex-shrink-0 w-4 h-4 fill-red-600" />
            </button>
          </td>

          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('campaigns.edit', campaign.id)">
              <img v-if="campaign.photo" class="block w-5 h-5 rounded-full mr-2 -my-2" :src="campaign.photo" alt="Photo" />
              {{ campaign.name }}
              <icon v-if="campaign.deleted_at" name="trash" class="flex-shrink-0 w-3 h-3 fill-gray-400 ml-2" />
            </inertia-link>
          </td>

          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('campaigns.edit', campaign.id)">
              {{ campaign.views }}
            </inertia-link>
          </td>

          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('campaigns.edit', campaign.id)">
              {{ campaign.city }}/{{ campaign.state }}
            </inertia-link>
          </td>

          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('campaigns.edit', campaign.id)">
              {{ customFormatter(campaign.start_date) }} a {{ customFormatter(campaign.end_date) }}
            </inertia-link>
          </td>

          <td class="border-t">
            <span class="text-xs px-2 py-0.5 font-bold bg-gray-100 text-gray-600 rounded">
              <a v-if="campaign.interaction" :href="campaign.interaction" target="_blank">
                {{ campaign.interaction }}
              </a>
            </span>
          </td>

          <td class="border-t">
            <span class="text-xs px-2 py-0.5 font-bold bg-gray-100 text-gray-600 rounded">
              <a v-if="campaign.url" :href="campaign.url" target="_blank">
                {{ campaign.url }}
              </a>
            </span>
          </td>

          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('campaigns.edit', campaign.id)" tabindex="-1">
              {{ customFormatter(campaign.created_at) }}
            </inertia-link>
          </td>

          <td v-if="showOrganizations" class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('campaigns.edit', campaign.id)" tabindex="-1">
              {{ campaign.organization }}
            </inertia-link>
          </td>

          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('campaigns.edit', campaign.id)" tabindex="-1">
              <active-badge :active="campaign.active" />
            </inertia-link>
          </td>

          <td class="border-t w-px">
            <inertia-link class="px-4 flex items-center" :href="route('campaigns.edit', campaign.id)" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </inertia-link>
          </td>
        </tr>

        <tr v-if="campaigns.data.length === 0">
          <td class="border-t px-6 py-4" colspan="6">Nenhum registro</td>
        </tr>
      </table>
    </div>

    <pagination class="mt-6" :links="campaigns.links" />
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import TextInput from '@/Shared/TextInput'
import ActiveBadge from '@/Shared/ActiveBadge'
import moment from 'moment'
import Pagination from '@/Shared/Pagination'

export default {
  metaInfo: { title: 'Campanhas' },
  components: {
    Icon,
    Pagination,
    ActiveBadge,
    TextInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    campaigns: Object,
    showOrganizations: Boolean,
  },
  data() {
    return {
      messages: [],
      form: {
        search: this.filters.search,
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(this.route('campaigns'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    destroy(id) {
      if(confirm('Tem certeza de que deseja excluir esta campanha?'))
      {
        this.$inertia.delete(this.route('campaigns.destroy', id))
      }
    },
    postMessages() {
      if(this.messages.length == 0)
      {
        return
      }

      this.$inertia.post(this.route('campaigns.msg'), {messages: this.messages})
    },
    setMessage(event, id) {
      if(!event.target?.checked)
      {
        this.messages.splice(this.messages.indexOf(id), 1)

        return
      }

      this.messages.push(id)
    },
    customFormatter(date) {
      moment.locale('pt-br')
      return moment(date).format('L')
    },
  },
}
</script>
